import ChevronRight from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { useLanguage } from '../../app/i18n';
import { StatusChip } from '../../components/StatusChip';
import { reasonMessage, type SupportedLanguage } from '../../i18n';
import { ChannelPhotoAvatar } from '../onboarding';
import { channelSettingsPath } from '../settings';
import { channelSettingLabel } from '../shared/channel-setting-label';

import type { CopiedChannel, CopiedChip, CopiedLine, CopiedRowView } from './channels-tab-model';

// dir="auto": a channel name in Arabic or Hebrew reads right to left (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

function chipLabel(chip: CopiedChip, t: TFunction): string {
  return chip === 'inactive'
    ? t('onboarding.channels.inactive')
    : t(`home.channels.copyState.${chip}`);
}

function lineText(line: CopiedLine, t: TFunction, language: SupportedLanguage): string | null {
  switch (line.kind) {
    case 'none':
      return null;
    case 'notConfigured':
      return t('home.channels.toConfigureHint');
    case 'summary':
      return channelSettingLabel(line.setting, t, language);
    case 'reason': {
      // I12: the catalogue text of the cause, never an engine or Telegram text.
      const message = reasonMessage(line.reasonCode);
      return t(message.key, message.values);
    }
  }
}

/**
 * A row of "Copied" (S9 R3 to R5, R7; `GestionCanaux.html`): one link to the channel's settings
 * (FXM-02 §4 "one gesture"), whose accessible name holds the name, the affiliate label, the line
 * and the status. Avatar and chevron are decorative.
 */
export function CopiedChannelRow({
  channel,
  view,
}: {
  readonly channel: CopiedChannel;
  readonly view: CopiedRowView;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const line = lineText(view.line, t, language);
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={RouterLink}
        to={channelSettingsPath(view.subscriptionId)}
        sx={{ gap: 1.5, py: 1.25, px: 1.75, minHeight: 44 }}
      >
        <ChannelPhotoAvatar channel={channel} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="span"
            dir={OWN_DIRECTION}
            sx={{
              display: 'block',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {channel.title}
          </Box>
          {channel.recommendedByAffiliate ? (
            <Box
              component="span"
              sx={(theme) => ({ display: 'block', fontSize: 13, color: theme.fx.textMuted })}
            >
              {t('onboarding.channels.affiliateChannel')}
            </Box>
          ) : null}
          {line === null ? null : (
            <Box
              component="span"
              sx={(theme) => ({
                display: 'block',
                fontSize: 13,
                color: view.line.kind === 'notConfigured' ? theme.fx.warning : theme.fx.textMuted,
              })}
            >
              {line}
            </Box>
          )}
        </Box>
        <StatusChip tone={view.tone}>{chipLabel(view.chip, t)}</StatusChip>
        <ChevronRight aria-hidden fontSize="small" sx={{ color: 'text.secondary' }} />
      </ListItemButton>
    </ListItem>
  );
}
